import { Injectable } from '@angular/core';
import { CheckinBaseClass } from '../../components';
import { FunctionIneligibleReason } from '../../master/function-ineligible-reason';
import { LOAD_MASTERS } from '../../master/master';
import { ApiErrorsInner, PageType } from '@lib/interfaces';
import { ErrorType } from '@lib/interfaces/errors';
import { ApiErrorResponseService, AswMasterService, ErrorsHandlerService, PageLoadingService } from '@lib/services';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, filter, map, withLatestFrom } from 'rxjs';
import {
  CheckinSearchResultState,
  resetCheckinSearchResult,
  selectCheckinSearchResultIsFailureStatus,
  selectCheckinSearchResultState,
  setCheckinSearchResultFromApi,
} from 'src/common/store';
import {
  DefaultService as Api,
  PostJourneysSearchJourneyRequest,
  PostJourneysSearchJourneyResponse,
} from 'src/sdk_checkin';
import { ErrorCodeConstants } from '@conf/app.constants';

@Injectable()
export class CheckinSearchResultStoreService extends CheckinBaseClass {
  private _result$: Observable<CheckinSearchResultState>;
  private _result!: CheckinSearchResultState;
  get result() {
    return this._result;
  }

  // 機能不可事由テーブルを取得するためのObservable
  private functionIneligibleReason$: Observable<FunctionIneligibleReason>;

  /**
   * チェックイン検索APIエラー一覧
   * key=エラーコード
   * value=エラーメッセージID
   */
  private static readonly ApiErrorMessages: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000004]: 'E0272',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000005]: 'E0258',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000006]: 'E0257',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000007]: 'E0273',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000008]: 'E0259',
    // NOTE EBAZ000009 は、APIのエラーレスポンスからメッセージIDを取得する
    [ErrorCodeConstants.ERROR_CODES.EBAZ000010]: 'E0276',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000398]: 'E0274',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000399]: 'E0798',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000892]: 'E1874',
  };

  /**
   * チェックイン検索APIエラー一覧(チェックイン状況詳細画面用)
   * key=エラーコード
   * value=エラーメッセージID
   */
  private static readonly ApiErrorMessagesForMybooking: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000004]: 'E1740',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000005]: 'E1741',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000006]: 'E1742',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000007]: 'E1743',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000008]: 'E1744',
    // NOTE EBAZ000009 は、APIのエラーレスポンスからメッセージIDを取得する
    [ErrorCodeConstants.ERROR_CODES.EBAZ000010]: 'E1745',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000398]: 'E1746',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000399]: 'E1747',
  };
  /** APIエラーSubscription */
  apiErrorResponseSubscription?: Subscription;

  constructor(
    private _store: Store<CheckinSearchResultState>,
    private _api: Api,
    private _apiErrorResponseService: ApiErrorResponseService,
    private _errorsHandlerService: ErrorsHandlerService,
    private _pageLoadingService: PageLoadingService,
    _aswMasterService: AswMasterService
  ) {
    super();
    this._result$ = this._store.pipe(
      select(selectCheckinSearchResultState),
      filter((data) => !!data)
    );
    this.subscribeService(this._result$, (data) => {
      this._result = data;
    });
    this.functionIneligibleReason$ = _aswMasterService
      .getAswMasterByKey$(LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON.key)
      .pipe(filter((state) => !!state));
  }

  override destroy(): void {
    if (!!this.apiErrorResponseSubscription) {
      this.apiErrorResponseSubscription.unsubscribe();
      this.apiErrorResponseSubscription = undefined;
    }
  }

  /**
   * CheckinSearchResultStateStore のObservableを返却する
   * @returns CheckinSearchResultStateStore の全ての値を取得するObservable
   */
  public getResultState$() {
    return this._result$;
  }

  /**
   * API呼び出しが失敗したかのObservableを返却する
   * @returns isFailureの値を取得するObservable
   */
  private getIsFailure$() {
    return this._store.pipe(select(selectCheckinSearchResultIsFailureStatus));
  }

  /**
   * チェックイン検索が正常終了したら結果を取得するObservable
   */
  getResultStateIfSuccessful$() {
    return this._result$.pipe(filter((state) => !state.isFailure && !state.isPending && !!state.model));
  }

  /**
   * API呼び出し結果のObservableを受け取りStoreに格納する
   * @param call API呼び出し結果を取得するObservable
   */
  private setResult(call: Observable<PostJourneysSearchJourneyResponse>) {
    this._store.dispatch(setCheckinSearchResultFromApi({ call }));
  }

  /**
   * チェックイン検索のストアをクリアする
   */
  public clear() {
    this._store.dispatch(resetCheckinSearchResult());
  }

  /**
   * チェックイン検索APIの呼び出しと、呼び出し結果のStoreへの設定を行う
   * @param condition チェックイン検索APIリクエスト情報
   * @param isMybooking チェックイン状況詳細画面空の呼び出しの場合true
   * @param isNotRetryableErrorOfCheckinSearchCommon チェックイン検索共通処理で、チェックイン検索APIでエラーになった場合に継続不可能エラーにするかどうか
   */
  callApi(
    condition: PostJourneysSearchJourneyRequest,
    isMybooking: boolean = false,
    isNotRetryableErrorOfCheckinSearchCommon: boolean = false
  ) {
    this.setupApiErrorHandler(isMybooking, isNotRetryableErrorOfCheckinSearchCommon);
    const call = this._api.journeysSearchJourneyPost(this.makeGetJourneyIdRequestBody(condition));
    this.setResult(call);
  }

  /**
   * チェックイン検索APIのエラーを監視して処理する
   * @param isMybooking チェックイン状況詳細画面空の呼び出しの場合true
   * @param isNotRetryableErrorOfCheckinSearchCommon チェックイン検索共通処理において、チェックイン検索APIでエラーになった場合に継続不可能エラーにするかどうか
   */
  private setupApiErrorHandler(isMybooking: boolean, isNotRetryableErrorOfCheckinSearchCommon: boolean) {
    if (!!this.apiErrorResponseSubscription) {
      return;
    }
    this.apiErrorResponseSubscription = this.getIsFailure$()
      .pipe(
        filter((state) => !!state),
        withLatestFrom(this._apiErrorResponseService.getApiErrorResponse$(), this.functionIneligibleReason$),
        map(([_, error, functionIneligibleReason]) => {
          const apiError = error?.errors?.[0];
          if (apiError && !isMybooking) {
            const matchErrorMsgId = this.getMessageId(apiError, functionIneligibleReason);
            if (matchErrorMsgId) {
              if (isNotRetryableErrorOfCheckinSearchCommon) {
                this._errorsHandlerService.setNotRetryableError({
                  errorType: ErrorType.BUSINESS_LOGIC,
                  errorMsgId: matchErrorMsgId,
                  apiErrorCode: apiError.code,
                });
              } else {
                this._errorsHandlerService.setRetryableError(PageType.PAGE, {
                  errorMsgId: matchErrorMsgId,
                  apiErrorCode: apiError.code,
                });
              }
            } else {
              this._errorsHandlerService.setNotRetryableError({
                errorType: ErrorType.SYSTEM,
                apiErrorCode: apiError.code,
              });
            }
          }

          if (apiError && isMybooking) {
            const matchErrorMsgIdForMybooking = this.getMessageIdForMybooking(apiError, functionIneligibleReason);
            if (matchErrorMsgIdForMybooking) {
              this._errorsHandlerService.setNotRetryableError({
                errorType: ErrorType.BUSINESS_LOGIC,
                errorMsgId: matchErrorMsgIdForMybooking,
                apiErrorCode: apiError.code,
              });
            } else {
              this._errorsHandlerService.setNotRetryableError({
                errorType: ErrorType.SYSTEM,
                apiErrorCode: apiError.code,
              });
            }
          }
          // ローディング画面表示終了
          this._pageLoadingService.endLoading();
        })
      )
      .subscribe();
  }

  /**
   * エラーメッセージIDの取得
   * @returns
   */
  getErrorMessageId$() {
    const apiErrorResponse$ = this._apiErrorResponseService.getApiErrorResponse$().pipe(filter((state) => !!state));
    return this.getIsFailure$().pipe(
      withLatestFrom(apiErrorResponse$, this.functionIneligibleReason$),
      map(([_, error, functionIneligibleReason]) => {
        const apiError = error?.errors?.[0];
        return !!apiError ? this.getMessageId(apiError, functionIneligibleReason) : undefined;
      })
    );
  }

  /**
   * チェックイン検索画面用エラーメッセージID取得処理
   * @param apiError APIエラーレスポンス
   * @param functionIneligibleReason 機能不可事由マスタ
   * @returns エラーメッセージID
   */
  private getMessageId(apiError: ApiErrorsInner, functionIneligibleReason: FunctionIneligibleReason) {
    if (apiError.code === ErrorCodeConstants.ERROR_CODES.EBAZ000009) {
      return this.getFunctionIneligibleReasonErrorId(apiError, functionIneligibleReason);
    } else {
      return CheckinSearchResultStoreService.ApiErrorMessages[apiError.code];
    }
  }

  /**
   * チェックイン状況詳細画面用エラーメッセージID取得処理
   * @param apiError APIエラーレスポンス
   * @param functionIneligibleReason 機能不可事由マスタ
   * @returns エラーメッセージID
   */
  private getMessageIdForMybooking(apiError: ApiErrorsInner, functionIneligibleReason: FunctionIneligibleReason) {
    if (apiError.code === ErrorCodeConstants.ERROR_CODES.EBAZ000009) {
      return this.getFunctionIneligibleReasonErrorId(apiError, functionIneligibleReason);
    } else {
      return CheckinSearchResultStoreService.ApiErrorMessagesForMybooking[apiError.code];
    }
  }

  /**
   * EBAZ000009用エラーメッセージID取得処理
   * @param apiError APIエラーレスポンス
   * @param functionIneligibleReason 機能不可事由マスタ
   * @returns エラーメッセージID
   */
  private getFunctionIneligibleReasonErrorId(
    apiError: ApiErrorsInner,
    functionIneligibleReason: FunctionIneligibleReason
  ) {
    const source = apiError.source as {
      items: string[];
    };
    // ASW不可事由まで展開した機能不可事由テーブル
    const functionIneligibleReasonByCheckin = functionIneligibleReason['checkin']['dxapiAcceptanceEligibility'];
    // エラーとして返されたdxapiReasons
    const DxApiIneligibleReason = source.items[0];
    return functionIneligibleReasonByCheckin[DxApiIneligibleReason]?.[0]?.error_message_id;
  }

  /**
   * JourneyIdを取得するためのRequest Bodyを作成する
   * @param checkinSearchCondition
   * @returns
   */
  private makeGetJourneyIdRequestBody(checkinSearchCondition: PostJourneysSearchJourneyRequest) {
    if (checkinSearchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.Order) {
      return {
        searchType: checkinSearchCondition.searchType,
        orderId: checkinSearchCondition.orderId,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    } else if (checkinSearchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.ETicket) {
      return {
        searchType: checkinSearchCondition.searchType,
        eTicketNumber: checkinSearchCondition.eTicketNumber,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    } else if (checkinSearchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.AmcMember) {
      return {
        searchType: checkinSearchCondition.searchType,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    } else {
      return {
        searchType: checkinSearchCondition.searchType,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    }
  }
}
